import React from 'react';
import { Stack, Button, Box, ListItem, List, ListItemIcon, Typography, Divider } from '@mui/material';
import { AutorenewOutlined } from '@mui/icons-material';
import { AnalyticsAccountChecklist, ErrorWithDescription } from 'types';
import { CopyText } from 'components/CopyText';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import ga_image_1 from 'assets/images/GA1.png';
import ga_image_2 from 'assets/images/GA2.png';
import ga_image_4 from 'assets/images/GA4.png';
import ga_image_5 from 'assets/images/GA5.png';
import ga_image_6 from 'assets/images/GA6.png';
import ga_image_7 from 'assets/images/GA7.png';
import ga_image_8 from 'assets/images/GA8.png';
import { BUTTON_HEIGHT_CONSTANT } from './form';

const ANALYTICS_ADMIN_EMAIL = 'analytics@rcplus-alloy-gcp.iam.gserviceaccount.com';
const GOOGLE_ANALYTICS_LINK = 'https://analytics.google.com';

const GA4ListItem = ({ text }: { text: string }) => (
  <ListItem sx={{ p: 0 }}>
    <ListItemIcon sx={(theme) => ({ minWidth: theme.spacing(4) })}>
      <FiberManualRecordRoundedIcon fontSize="small" sx={{ color: 'text.primary', fontSize: 10 }} />
    </ListItemIcon>
    <Typography variant="body1">{text}</Typography>
  </ListItem>
);

export const additionalErrorTexts: ErrorWithDescription = {
  title: 'Einstellung überprüfen',
  description:
    'Überprüfe die Property ID in Schritt 2 und die Einstellungen in Schritt 3. Ein Fehler in einem dieser Bereiche könnte die Verbindung verhindern.',
};

export const GA4ScreenshotImage = ({ src }: { src: string }) => (
  <Box
    component={'img'}
    src={src}
    alt={'GA4 Screenshot'}
    maxWidth={600}
    minWidth={250}
    width="100%"
    border={(theme) => `4px solid ${theme.palette.grey[400]}`}
    borderRadius={1.5}
  />
);

export const inputAccordionData = (
  <>
    <Stack gap={4}>
      <Typography variant="h4">Öffne die Admin-Einstellungen in deinem GA4-Konto.</Typography>
      <Typography variant="body1">Du findest sie unten links in der GA4-Anwendung.</Typography>
    </Stack>
    <Stack gap={5}>
      <Typography variant="h4">Wähle das richtige Konto und die gewünschte Property aus:</Typography>
      <GA4ScreenshotImage src={ga_image_1} />
      <Divider sx={{ opacity: 0 }} />
      <GA4ScreenshotImage src={ga_image_2} />
    </Stack>
    <Stack gap={4}>
      <Typography variant="h4">Kopiere die gewünschte Property ID</Typography>
      <Typography variant="body1" marginBottom={1}>
        Navigiere zu „Property Details“, kopiere die Property-ID, auf die Ringier Advertising Zugriff erhalten soll, und
        füge sie in Schritt 2 in das entsprechende Feld ein.
      </Typography>
      <GA4ScreenshotImage src={ga_image_8} />
    </Stack>
  </>
);

export const getAnalyticsAccountChecklist = ({
  checkGa4ConnectionMutation,
  PropertyIdInput,
  setInputError,
  ga4PropertyId,
  shouldShowAdditionalError,
}: {
  checkGa4ConnectionMutation: () => void;
  PropertyIdInput: (() => JSX.Element) | null;
  setInputError?: () => void;
  ga4PropertyId?: string;
  shouldShowAdditionalError?: boolean;
}): AnalyticsAccountChecklist => [
  {
    title: '1. Schritt - Melde dich in deinem Google Analytics Konto an',
    description: '',
    additionalError: null,
    Content: () => (
      <Button
        LinkComponent={'a'}
        target="_blank"
        sx={(theme) => ({ width: 'fit-content', height: theme.spacing(BUTTON_HEIGHT_CONSTANT) })}
        endIcon={<OpenInNewRoundedIcon />}
        href={GOOGLE_ANALYTICS_LINK}
      >
        google analytics öffnen
      </Button>
    ),
  },
  {
    title: '2. Schritt - Gib hier die GA4 Property-ID ein',
    description: 'Gib die Property-ID ein zu der Ringier Advertising  Zugang erhalten soll.',
    additionalError: shouldShowAdditionalError ? additionalErrorTexts : null,
    Content: () => (PropertyIdInput ? <PropertyIdInput /> : null),
    accordion: {
      Content: () => inputAccordionData,
    },
  },
  {
    title: '3. Schritt - Alloy als Nutzer hinzufügen und Berechtigungen festlegen',
    additionalError: shouldShowAdditionalError ? additionalErrorTexts : null,
    description: 'Folgende E-Mail-Adresse kopieren und als neuen Nutzer zu deinem GA4-Konto hinzufügen.',
    Content: () => (
      <Stack gap={2}>
        <CopyText buttonText="E-Mail-Adresse kopieren" textToCopy={ANALYTICS_ADMIN_EMAIL} bold />
        <List>
          <Typography variant="body1">Lege folgende Rolle und Berechtigungen fest:</Typography>
          <GA4ListItem text="Viewer" />
          <GA4ListItem text="No Cost Metrics" />
          <GA4ListItem text="No Revenue Metrics" />
        </List>
      </Stack>
    ),
    accordion: {
      Content: () => (
        <>
          <Stack gap={4}>
            <Typography variant="h4">Alloy als neuen Nutzer hinzufügen</Typography>
            <List>
              <GA4ListItem text="Klicke auf “Property Access Management”" />
              <GA4ListItem text="Klicke auf den blauen “+”-Button und wähle “Benutzer hinzufügen” aus dem Dropdown-Menu." />
              <GA4ListItem text="Gib im ersten Feld folgende E-Mail-Adresse ein:" />
            </List>
            <CopyText buttonText="E-Mail-Adresse kopieren" textToCopy={ANALYTICS_ADMIN_EMAIL} bold />
          </Stack>
          <Stack gap={5}>
            <Typography variant="h4">Alloy als neuen Nutzer hinzufügen:</Typography>
            <GA4ScreenshotImage src={ga_image_4} />
            <Divider sx={{ opacity: 0 }} />
            <GA4ScreenshotImage src={ga_image_5} />
            <Divider sx={{ opacity: 0 }} />
            <GA4ScreenshotImage src={ga_image_6} />
          </Stack>
          <Stack gap={5}>
            <Stack gap={4}>
              <Typography variant="h4">Lege folgende Rolle und Berechtigungen fest:</Typography>
              <List sx={{ p: 0 }}>
                <GA4ListItem text="Viewer" />
                <GA4ListItem text="No Cost Metrics" />
                <GA4ListItem text="No Revenue Metrics" />
              </List>
            </Stack>
            <GA4ScreenshotImage src={ga_image_7} />
          </Stack>
        </>
      ),
    },
  },
  {
    title: 'Schritt 4 - Verbindung prüfen',
    description: 'Hast du alle Schritte oben ausgeführt?\nSuper! Dann lass und jetzt die Verbindung überprüfen.',
    additionalError: null,
    Content: () => (
      <Stack alignItems="start">
        <Button
          variant="contained"
          startIcon={<AutorenewOutlined />}
          onMouseUp={() => {
            if (setInputError && !ga4PropertyId) {
              setInputError();
            } else {
              checkGa4ConnectionMutation();
            }
          }}
        >
          Verbindung prüfen
        </Button>
      </Stack>
    ),
  },
];
